import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Badge,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import UpgradePlanButton from '../../UpgradePlanButton';
import Hotspot from '../../Hotspot';

import { userCanSeeVersions } from '../../../helpers/Auth';
import { userHasFeature } from '../../../helpers/Features';
import { fileSupportsFaceRecognition } from '../../../helpers/Files';
import { getSetting } from '../../../helpers/Settings';

const FileSidebarNav = ({
  handleSidebarTab,
  file,
  sidebarTab,
  shareUuid,
  share,
  showComments,
  showTags,
  editions,
  fullEditionsLoaded,
  isInTrial,
  comments,
}) => {
  const [alignLeft, setAlignLeft] = useState(false);

  const intl = useIntl();

  const navRef = useRef();

  useEffect(() => {
    const observe = () => {
      if (navRef.current) {
        if (navRef.current.children.length < 4) setAlignLeft(true);
      }
    };

    const observer = new MutationObserver(observe);
    observe();

    const observerOptions = {
      childList: true,
      subtree: false,
    };

    if (navRef.current) {
      observer.observe(navRef.current, observerOptions);
    }

    return () => {
      observer.disconnect();
    };
  }, [navRef]);

  const editionsCount = fullEditionsLoaded ? editions.length : file.editions_count;
  const faces = file.faces ? file.faces.filter(f => (!f.hidden)).length : 0;
  let commentsCount = 0;
  if (showComments) {
    if (file.comments_count?.all > 0) {
      commentsCount = file.comments_count?.all;
    }
    if (comments.length > 0) {
      commentsCount = comments.length;
    }
  }

  return (
    <div
      className={classnames('file-sidebar-nav', { 'file-sidebar-nav-left': alignLeft })}
      ref={navRef}
    >
      <Button
        size="sm"
        color="link"
        id="btn-details"
        className={classnames({ active: (sidebarTab === 'details' || sidebarTab === 'history') })}
        onClick={() => handleSidebarTab('details')}
      >
        <i className="fal fa-circle-info fa-fw" />
        <div className="slide-container">
          <div className="slide-inner">
            <span>{intl.formatMessage({ defaultMessage: 'Details' })}</span>
          </div>
        </div>
      </Button>
      {(!(sidebarTab === 'details' || sidebarTab === 'history')) && (
        <UncontrolledTooltip fade={false} target="btn-details" placement="top" boundariesElement="window">
          {intl.formatMessage({ defaultMessage: 'Details' })}
        </UncontrolledTooltip>
      )}
      {(
        (shareUuid && share?.show_people) || (
          !shareUuid &&
          (fileSupportsFaceRecognition(file) || sidebarTab === 'people') &&
          (getSetting('face_recognition') || isInTrial || !userHasFeature('face-recognition')) &&
          getSetting('face_recognition')
        )
      ) && (
        <>
          <Button
            size="sm"
            color="link"
            id="btn-people"
            className={classnames({
              active: sidebarTab === 'people',
              disabled: !userHasFeature('face-recognition') && !share?.show_people,
            })}
            onClick={() => handleSidebarTab('people')}
          >
            <i className="fal fa-user-viewfinder fa-fw" style={{ position: 'relative' }}>
              <Hotspot id="people-single-file" />
            </i>
            <div className="slide-container">
              <div className="slide-inner">
                <span>{intl.formatMessage({ defaultMessage: 'People' })}</span>
              </div>
            </div>
            <UpgradePlanButton
              feature="face-recognition"
              simple={true}
              flying={true}
            />
            {(!isInTrial && (userHasFeature('face-recognition') || share?.show_people) && faces > 0) && (
              <Badge>
                {faces}
              </Badge>
            )}
          </Button>
          {sidebarTab !== 'people' && (
            <UncontrolledTooltip fade={false} target="btn-people" placement="top" boundariesElement="window">
              {intl.formatMessage({ defaultMessage: 'People' })}
            </UncontrolledTooltip>
          )}
        </>
      )}
      {showTags && (
        <>
          <Button
            size="sm"
            color="link"
            id="btn-tags"
            className={classnames({ active: sidebarTab === 'tags' })}
            onClick={() => handleSidebarTab('tags')}
          >
            <i className="fal fa-tags fa-fw" />
            <div className="slide-container">
              <div className="slide-inner">
                <span>{intl.formatMessage({ defaultMessage: 'Tags' })}</span>
              </div>
            </div>
          </Button>
          {sidebarTab !== 'tags' && (
            <UncontrolledTooltip fade={false} target="btn-tags" placement="top" boundariesElement="window">
              {intl.formatMessage({ defaultMessage: 'Tags' })}
            </UncontrolledTooltip>
          )}
        </>
      )}
      {!shareUuid && (
        <>
          <Button
            size="sm"
            color="link"
            id="btn-editions"
            className={classnames({
              active: sidebarTab === 'editions',
              disabled: !userHasFeature('image-editor'),
            })}
            onClick={() => handleSidebarTab('editions')}
          >
            <i className="fal fa-crop fa-fw" />
            <div className="slide-container">
              <div className="slide-inner">
                <span>{intl.formatMessage({ defaultMessage: 'Editions' })}</span>
              </div>
            </div>
            <UpgradePlanButton
              feature="image-editor"
              simple={true}
              flying={true}
            />
            {(!isInTrial && userHasFeature('image-editor') && editionsCount !== undefined && editionsCount > 0) && (
              <Badge>
                {editionsCount}
              </Badge>
            )}
          </Button>
          {sidebarTab !== 'editions' && (
            <UncontrolledTooltip fade={false} target="btn-editions" placement="top" boundariesElement="window">
              {intl.formatMessage({ defaultMessage: 'Editions' })}
            </UncontrolledTooltip>
          )}
        </>
      )}
      {((shareUuid && share?.allow_versions) || (!shareUuid && userCanSeeVersions(file))) && (
        <>
          <Button
            size="sm"
            color="link"
            id="btn-versions"
            className={classnames({
              active: sidebarTab === 'versions',
              disabled: !userHasFeature('file-versions') && !(shareUuid && share?.allow_versions),
            })}
            onClick={() => handleSidebarTab('versions')}
          >
            <i className="fal fa-rectangle-history fa-fw" />
            <div className="slide-container">
              <div className="slide-inner">
                <span>{intl.formatMessage({ defaultMessage: 'Versions' })}</span>
              </div>
            </div>
            <UpgradePlanButton
              feature="file-versions"
              simple={true}
              flying={true}
            />
            {(!isInTrial && userHasFeature('file-versions') && file.versions.length > 0) && (
              <Badge>
                {file.versions.length}
              </Badge>
            )}
          </Button>
          {sidebarTab !== 'versions' && (
            <UncontrolledTooltip fade={false} target="btn-versions" placement="top" boundariesElement="window">
              {intl.formatMessage({ defaultMessage: 'Versions' })}
            </UncontrolledTooltip>
          )}
        </>
      )}
      {showComments && (
        <>
          <Button
            size="sm"
            color="link"
            id="btn-comments"
            className={classnames({ active: sidebarTab === 'comments' })}
            onClick={() => handleSidebarTab('comments')}
          >
            <i className="fal fa-comment fa-fw" />
            <div className="slide-container">
              <div className="slide-inner">
                <span>{intl.formatMessage({ defaultMessage: 'Comments' })}</span>
              </div>
            </div>
            {commentsCount > 0 && (
              <Badge>
                {commentsCount}
              </Badge>
            )}
          </Button>
          {sidebarTab !== 'comments' && (
            <UncontrolledTooltip fade={false} target="btn-comments" placement="top" boundariesElement="window">
              {intl.formatMessage({ defaultMessage: 'Comments' })}
            </UncontrolledTooltip>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  filesLoading: state.Files.loading,
  share: state.Shares.share,
  editions: state.Files.fileEditions,
  isInTrial: state.Auth.profile?.client?.is_in_trial,
  comments: state.Files.comments,
});

FileSidebarNav.propTypes = {
  handleSidebarTab: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  sidebarTab: PropTypes.string.isRequired,
  shareUuid: PropTypes.string,
  share: PropTypes.object,
  showComments: PropTypes.bool.isRequired,
  showTags: PropTypes.bool.isRequired,
  editions: PropTypes.array.isRequired,
  fullEditionsLoaded: PropTypes.bool.isRequired,
  isInTrial: PropTypes.bool,
  comments: PropTypes.array.isRequired,
};

FileSidebarNav.defaultProps = {
  shareUuid: null,
  share: null,
  isInTrial: false,
};

export default connect(mapStateToProps)(FileSidebarNav);
